import { useCallback } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import type { SupportedThirdParty } from '../../common/constants/import-type';
import type { ProjectData } from '../../common/types';
import { useStartX2CImport } from '../../services/start-import';
import { useStartThirdPartyImport } from '../../services/start-third-party-import';
import {
	useMigrationId,
	useAdvancedOptions,
	useThirdPartyState,
	useMappingState,
	useSource,
	useImportType,
} from '../import-state/selectors';
import { useAccessTokenModalState } from '../ui-state';

type StartImportProps = {
	projectData: ProjectData;
	onNext: () => void;
};

export type UseStartImportController = {
	startImport: (props: StartImportProps) => void;
	isImportLoading: boolean;
	hasImportErrorOccurred: boolean;
	isStartImportDisabled: boolean;
};

type UseStartImportProps = {
	thirdParty?: SupportedThirdParty;
	isMultipleEntitiesSupported?: boolean;
};

export const useStartImportController = ({
	thirdParty,
	isMultipleEntitiesSupported = false,
}: UseStartImportProps = {}): UseStartImportController => {
	const [migrationId, { setMigrationId }] = useMigrationId();
	const [
		{
			selectedEntityId,
			userEmails,
			thirdPartyFieldConfigState,
			thirdPartyStatusMapping,
			thirdPartyImportSettings,
			selectedWorkspaceId,
			selectedEntities,
		},
	] = useThirdPartyState();
	const [mappingState] = useMappingState();
	const [advancedOptions] = useAdvancedOptions();
	const [source] = useSource();
	const [serviceName] = useImportType();
	const { isLoading, hasErrorOccurred, startX2CImport } = useStartX2CImport(source, serviceName);
	const [, { setRequestAuthenticationToken }] = useAccessTokenModalState();

	const {
		isLoading: isLoadingThirdPartyImport,
		hasErrorOccurred: hasThirdPartyErrorOccurred,
		startThirdPartyImport,
	} = useStartThirdPartyImport({
		thirdParty,
		requestAuthenticationToken: setRequestAuthenticationToken,
	});

	const isImportLoading = isLoading || isLoadingThirdPartyImport;
	const hasImportErrorOccurred = hasErrorOccurred || hasThirdPartyErrorOccurred;
	const entityIsSelected = isMultipleEntitiesSupported
		? (selectedEntities?.length || 0) > 0
		: selectedEntityId !== null;
	const isStartImportDisabled =
		(mappingState === null || migrationId === null) && !entityIsSelected;

	const startImport = useCallback(
		({ projectData, onNext }: StartImportProps) => {
			const onThirdPartyImportSuccess = (migrationIdThirdParty: string) => {
				setMigrationId(migrationIdThirdParty);
				onNext();
			};

			if (entityIsSelected && thirdParty) {
				startThirdPartyImport({
					projectData,
					selectedEntityId:
						(isMultipleEntitiesSupported
							? selectedEntities && selectedEntities[0].id
							: selectedEntityId) || '',
					selectedEntities,
					userEmails,
					thirdPartyConfigState: thirdPartyFieldConfigState,
					thirdPartyStatusMapping,
					importSettings: thirdPartyImportSettings,
					onSuccess: onThirdPartyImportSuccess,
					parentEntityId: fg('jwm_clickup_importer') ? selectedWorkspaceId || undefined : undefined,
					isMultiEntity: isMultipleEntitiesSupported,
				});
				return;
			}

			if (migrationId !== null && mappingState !== null) {
				startX2CImport(migrationId, mappingState, projectData, advancedOptions, onNext);
			}
		},
		[
			advancedOptions,
			entityIsSelected,
			isMultipleEntitiesSupported,
			mappingState,
			migrationId,
			selectedEntities,
			selectedEntityId,
			selectedWorkspaceId,
			setMigrationId,
			startThirdPartyImport,
			startX2CImport,
			thirdParty,
			thirdPartyFieldConfigState,
			thirdPartyImportSettings,
			thirdPartyStatusMapping,
			userEmails,
		],
	);
	return { startImport, isImportLoading, hasImportErrorOccurred, isStartImportDisabled };
};
